import * as React from 'react';
import Moment from 'moment';
import './SchoolEventAwardsSummary.scss';

interface AwardTypeLite {
  name: string;
  picture?: string | null;
}

interface PlayerLite {
  userId?: string;
  permissionId?: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
}

interface Props {
  awards: any[];
  awardTypesById: Record<string, AwardTypeLite>;
  players: PlayerLite[];
  onGrantAwardsClick?: () => void;
  onEditAwardClick?: (award: any) => void;
  onDeleteAwardClick?: (award: any) => void;
}

const AWARD_LOGO_PLACEHOLDER = '/dist/images/empty_pic_uploader_box.png';

const TrophyIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0z" />
    <path d="M7 6H4a2 2 0 0 0 2 4h1M17 6h3a2 2 0 0 1-2 4h-1" />
  </svg>
);

const EditIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M3 6h18M8 6V4h8v2M6 6l1 16h10l1-16" />
    <path d="M10 11v6M14 11v6" />
  </svg>
);

const getStudentKey = (player: PlayerLite) => `${player.userId || ''}|${player.permissionId || ''}`;

const getStudentName = (award: any, playerByKey: Map<string, PlayerLite>) => {
  const uid = award?.student?.userId || award?.student?.id;
  const pid = award?.student?.permissionId;
  if (!uid) {
    return 'House award';
  }

  const player = playerByKey.get(`${uid}|${pid}`) || playerByKey.get(`${uid}|`);
  if (!player) {
    return (
      award?.student?.displayName ||
      [award?.student?.firstName, award?.student?.lastName].filter(Boolean).join(' ') ||
      'Student'
    );
  }

  return (
    (player.displayName && player.displayName.trim()) ||
    [player.firstName, player.lastName]
      .filter(Boolean)
      .join(' ')
      .trim() ||
    'Student'
  );
};

const getType = (award: any, awardTypesById: Record<string, AwardTypeLite>) => {
  const typeId = award?.awardTypeId || award?.awardType?.id;
  return (typeId && awardTypesById[typeId]) || award?.awardType || null;
};

const getAwardTitle = (award: any, awardTypesById: Record<string, AwardTypeLite>) => {
  const type = getType(award, awardTypesById);
  return type?.name || 'Award';
};

const getAwardLogo = (award: any, awardTypesById: Record<string, AwardTypeLite>) => {
  const type = getType(award, awardTypesById);
  return type?.picture || AWARD_LOGO_PLACEHOLDER;
};

const getAwardPoints = (award: any) => {
  const points = Number(award?.points || 0);
  return Number.isFinite(points) ? points : 0;
};

export const SchoolEventAwardsSummary: React.FC<Props> = props => {
  const { awards, awardTypesById, players } = props;
  const playerByKey = new Map(players.map(player => [getStudentKey(player), player]));
  const totalPoints = awards.reduce((sum, award) => sum + getAwardPoints(award), 0);
  const studentsAwarded = new Set(awards.map(award => award?.student?.userId).filter(Boolean)).size;
  const houseAwards = awards.filter(award => !award?.student?.userId && award?.houseId).length;

  return (
    <div className="eAwards">
      <div className="eAwardsHeader">
        <div>
          <h2 className="eAwardsHeader__title">Awards</h2>
          <div className="eAwardsHeader__subtitle">Awards and house points granted from this event.</div>
        </div>
        {props.onGrantAwardsClick ? (
          <div className="eAwardsHeader__actions">
            <button type="button" className="sit-btn sit-btn--primary sit-btn--sm" onClick={props.onGrantAwardsClick}>
              Grant awards / house points
            </button>
          </div>
        ) : null}
      </div>

      <div className="eAwardsSummary">
        <div className="eAwardsSummaryChip eAwardsSummaryChip--awards">
          <span className="eAwardsSummaryChip__dot" />
          <span className="eAwardsSummaryChip__label">Awards</span>
          <span className="eAwardsSummaryChip__value">{awards.length}</span>
        </div>
        <div className="eAwardsSummaryChip eAwardsSummaryChip--students">
          <span className="eAwardsSummaryChip__dot" />
          <span className="eAwardsSummaryChip__label">Students</span>
          <span className="eAwardsSummaryChip__value">{studentsAwarded}</span>
        </div>
        <div className="eAwardsSummaryChip eAwardsSummaryChip--points">
          <span className="eAwardsSummaryChip__dot" />
          <span className="eAwardsSummaryChip__label">Points</span>
          <span className="eAwardsSummaryChip__value">{totalPoints}</span>
        </div>
        <div className="eAwardsSummaryChip eAwardsSummaryChip--house">
          <span className="eAwardsSummaryChip__dot" />
          <span className="eAwardsSummaryChip__label">House awards</span>
          <span className="eAwardsSummaryChip__value">{houseAwards}</span>
        </div>
      </div>

      <div className="eAwardsList">
        {awards.length === 0 ? (
          <div className="eAwardsEmpty">
            <span className="eAwardsEmpty__icon">
              <TrophyIcon />
            </span>
            <div>No awards yet for this event.</div>
          </div>
        ) : (
          awards.map(award => {
            const title = getAwardTitle(award, awardTypesById);
            const logo = getAwardLogo(award, awardTypesById);
            const studentName = getStudentName(award, playerByKey);
            const awardedAt = award?.awardedAt ? Moment(award.awardedAt).format('D MMM YYYY') : 'No date';
            const by = award?.awardedByName || 'Unknown';
            const points = getAwardPoints(award);

            return (
              <div className="eAwardsRow" key={award.id}>
                <div className="eAwardsRow__award">
                  <span className="eAwardsRow__logo">
                    <img
                      src={logo}
                      alt=""
                      onError={e => {
                        (e.currentTarget as HTMLImageElement).src = AWARD_LOGO_PLACEHOLDER;
                      }}
                    />
                  </span>
                  <div className="eAwardsRow__copy">
                    <div className="eAwardsRow__title">{title}</div>
                    <div className="eAwardsRow__meta">{studentName}</div>
                    <div className="eAwardsRow__submeta">
                      {awardedAt} · by {by}
                    </div>
                  </div>
                </div>

                <div className={points >= 0 ? 'eAwardsPoints eAwardsPoints--positive' : 'eAwardsPoints'}>
                  {points > 0 ? '+' : ''}
                  {points}
                </div>

                {props.onEditAwardClick || props.onDeleteAwardClick ? (
                  <div className="eAwardsRow__actions">
                    {props.onEditAwardClick ? (
                      <button
                        type="button"
                        className="eAwardsIconButton"
                        aria-label="Edit award"
                        onClick={() => props.onEditAwardClick(award)}
                      >
                        Edit
                      </button>
                    ) : null}
                    {props.onDeleteAwardClick ? (
                      <button
                        type="button"
                        className="eAwardsIconButton eAwardsIconButton--danger"
                        aria-label="Delete award"
                        onClick={() => props.onDeleteAwardClick(award)}
                      >
                        Delete
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

SchoolEventAwardsSummary.displayName = 'SchoolEventAwardsSummary';
